import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { MotionConfig } from 'motion/react'
import { AuthProvider } from './contexts/AuthContext'
import { InterviewFlow } from './pages/InterviewFlow'
import { AuthCallback } from './pages/AuthCallback'
import { LoginPage } from './pages/LoginPage'
import { EmailVerified } from './pages/EmailVerified'
import { ForgotPassword } from './pages/ForgotPassword'
import { ResetPassword } from './pages/ResetPassword'
import { MyInterviews } from './pages/MyInterviews'
import { AdminDashboard } from './pages/AdminDashboard'
import { SessionDetail } from './pages/SessionDetail'
import { CandidateSessionReport } from './pages/CandidateSessionReport'
import { RolePicker } from './pages/RolePicker'
import { RecruiterDashboard } from './pages/RecruiterDashboard'
import { RecruiterCompare } from './pages/RecruiterCompare'
import { InviteBuilder } from './pages/InviteBuilder'
import { InviteDetail } from './pages/InviteDetail'
import { InviteInterview } from './pages/InviteInterview'
import { SharedReportPage } from './pages/SharedReportPage'
import { NotFound } from './pages/NotFound'
import { CandidateProfile } from './pages/CandidateProfile'
import { RecruiterProfile } from './pages/RecruiterProfile'
import { CandidateDashboard } from './pages/CandidateDashboard'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
// The marketing page carries the scroll-motion engine (gsap + lenis) and its
// own stylesheet — kept in a dedicated chunk so the app bundle's first load
// doesn't pay for them.
const Landing = lazy(() =>
  import('./pages/Landing').then((m) => ({ default: m.Landing })),
)
function App() {
  return (
    <AuthProvider>
      {/* reducedMotion="user": framer-motion disables transform and layout
          animations when the OS asks for reduced motion (opacity still
          fades — the CSS kill-switch in aura-base.css covers the rest, and
          its instant state changes also apply to JS motion like tickers). */}
      <MotionConfig reducedMotion="user">
        <Routes>
        {/* Public marketing world — anonymous-only; logged-in users are
            redirected into the app by Landing itself. */}
        <Route path="/" element={<Suspense fallback={null}><Landing /></Suspense>} />

        {/* Auth world (own centered layout, no app chrome). */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/verified" element={<EmailVerified />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Candidate flow pages: the live interview owns the whole screen
            (arena HUD), the flow steps use the minimal header. */}
        <Route path="/interview" element={<ProtectedRoute><InterviewFlow /></ProtectedRoute>} />
        {/* Standalone candidate page reached while logged out of the app. */}
        <Route path="/invite/:token" element={<InviteInterview />} />
        {/* Public shared report: the token is the whole credential, so this
            renders for a logged-out stranger — shell-less and read-only. */}
        <Route path="/share/report/:token" element={<SharedReportPage />} />

        {/* App world — every page below renders the AppShell workspace
            chrome itself (sidebar + content, no topbar), passing its own
            active nav highlight and optional in-content title. */}
        <Route path="/dashboard" element={<ProtectedRoute><CandidateDashboard /></ProtectedRoute>} />
        <Route element={<ProtectedRoute />}>
          <Route path="/my-interviews" element={<MyInterviews />} />
          <Route path="/my-interviews/:sessionId" element={<CandidateSessionReport />} />
          <Route path="/choose-role" element={<RolePicker />} />
          <Route path="/profile/candidate" element={<CandidateProfile />} />
        </Route>
        <Route element={<ProtectedRoute requireRole="recruiter" />}>
          <Route path="/recruiter" element={<RecruiterDashboard />} />
          <Route path="/recruiter/compare" element={<RecruiterCompare />} />
          <Route path="/recruiter/new" element={<InviteBuilder />} />
          <Route path="/recruiter/invites/:inviteId" element={<InviteDetail />} />
          <Route path="/profile/recruiter" element={<RecruiterProfile />} />
        </Route>
        <Route element={<ProtectedRoute requireAdmin />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/session/:sessionId" element={<SessionDetail />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
      </MotionConfig>
    </AuthProvider>
  )
}

export default App
